<template>
    <v-app ref="formContainer">
        <breadcrumb :items="breadcrumbs"></breadcrumb>
        <v-container v-if="!reportId" fluid>
            <div class="d-flex mb-4">
                <h2>{{ $t("esgPlatform.esgReport.createEsgReport") }}</h2>
                <v-spacer></v-spacer>
                <v-btn color="primary" outlined class="mr-2 btn-outline-primary font-weight-bold mb-2"
                     @click="showCloseConfirmation = true">
                     {{ $t('esgPlatform.esgReport.close') }}
                </v-btn>
                <!-- template setting button for step 2  -->
                <div v-if="step === 2" class="d-flex align-center">
                    <v-btn color="primary" dark class="mr-2 mb-2" @click="showTemplateSettings = true">
                        {{ $t('esgPlatform.esgReport.templateSettings') }}
                    </v-btn>
                    <v-btn color="primary" dark class="mr-2 mb-2" @click="step = 3">
                        {{ $t('esgPlatform.esgReport.next') }}
                    </v-btn>
                </div>
                <!-- for step 3 -->
                <div v-else-if="step === 3"  class="d-flex align-center">
                    <!-- back  -->
                    <v-btn color="primary" dark class="mr-2 mb-2" @click="step = 2">
                        {{ $t('esgPlatform.esgReport.back') }}
                    </v-btn>
                    <!-- <v-btn color="primary" dark class="mr-2 mb-2" @click="showPublishConfirmation = true">
                        {{ $t('Publish To Dimitra') }}
                    </v-btn>
                    <v-btn color="primary" dark class="mr-2 mb-2" @click="showDialog = true">
                        {{ $t('Publish To Blockchain') }}
                    </v-btn> -->
                    <v-btn color="primary" dark class="mr-2 mb-2" @click="showSaveConfirmation = true">
                        {{ $t('esgPlatform.esgReport.save') }}
                    </v-btn>
                </div>
            </div>

            <v-card>
                <v-card-text>
                    <!-- Replace v-stepper with custom stepper -->
                    <CustomStepper :current-step="step" :steps="stepperSteps" @step-changed="handleStepChange"
                        class="mb-4" />

                    <!-- Step Content -->
                    <div v-if="step === 1" class="mb-9">
                        <v-card flat>
                            <v-card-text>
                                <report-details :protocolId="selectedProtocolId" :report-type="reportType"
                                    :timeline="timeline" @update:protocol="selectedProtocol = $event"
                                    @update:report-type="reportType = $event" @update:timeline="timeline = $event"
                                    @protocol-selected-id="handleProtocolSelection"></report-details>
                                <!-- Report Configuration Section -->
                                <v-card class="mt-4 pa-6" elevation="0" flat>
                                    <v-card-text>
                                        <v-row>
                                            <v-col cols="6" md="3">
                                                <h3 class="mb-3">{{ $t('esgPlatform.esgReport.reportTypeSelection') }}</h3>
                                                <v-radio-group v-model="reportType"
                                                    @change="$emit('update:report-type', reportType)">
                                                    <v-radio :label="$t('esgPlatform.esgReport.internalReport')" value="Internal Report"></v-radio>
                                                    <v-radio :label="$t('esgPlatform.esgReport.publicReport')" value="Public Report"></v-radio>
                                                </v-radio-group>
                                            </v-col>
                                            <v-col cols="6" md="3">
                                                <h3 class="mb-3">{{ $t('esgPlatform.esgReport.reportTimeline') }}</h3>
                                                <div class="d-flex align-center mb-2">
                                                    <span>{{ $t('esgPlatform.esgReport.selectTimeline') }}</span>
                                                    <v-icon color="green" class="ml-2">mdi-information</v-icon>
                                                </div>
                                                <v-select 
                                                    v-model="timeline" 
                                                    :items="timelineOptions" 
                                                    outlined 
                                                    dense
                                                    multiple
                                                    chips
                                                    deletable-chips
                                                    small-chips
                                                    @change="$emit('update:timeline', timeline)"
                                                ></v-select>
                                            </v-col>
                                        </v-row>
                                    </v-card-text>
                                </v-card>
                                <sub-organizations :sub-orgs="subOrganizations" :protocol-id="selectedProtocolId"
                                    @update:sub-orgs="subOrganizations = $event"></sub-organizations>
                                <template-selection :templates="templates"
                                    @update:selected-template="selectedTemplate = $event"></template-selection>
                            </v-card-text>
                            <v-card-actions>
                                <v-btn color="primary" @click="handleCreateEsg()">{{ $t('esgPlatform.esgReport.next') }}</v-btn>
                            </v-card-actions>
                        </v-card>
                    </div>

                    <!-- Step 2 -->
                    <div v-else-if="step === 2">
                        <v-card flat>
                            <v-card-text>
                                <label for="templateName">{{ $t('esgPlatform.esgReport.templateName') }}</label>
                                <div class="my-4">
                                    <v-text-field 
                                        outlined
                                        :placeholder="$t('Name of Template')"
                                        dense 
                                        class="shrink" 
                                        v-model="templateName"
                                    ></v-text-field>
                                </div>
                                <div>
                                    <SectionItem v-for="(section, idx) in sections" :key="idx" :section="section"
                                        :index="idx" :protocol-id="selectedProtocolId" :target-years="timeline"
                                        :sub-organizations="subOrganizations"
                                        @delete-section="deleteSection" @move-section-up="moveSectionUp"
                                        @move-section-down="moveSectionDown" @reorder-blocks="handleReorderBlocks" />
                                    <AddNewButton color="green" @click="addSection()">{{ $t('esgPlatform.esgReport.addNewSection') }}
                                    </AddNewButton>
                                </div>
                            </v-card-text>
                            <v-card-actions>
                                <v-btn color="primary" @click="step = 1">{{ $t('esgPlatform.esgReport.back') }}</v-btn>
                                <v-btn color="primary" @click="step = 3">{{ $t('esgPlatform.esgReport.next') }}</v-btn>
                            </v-card-actions>
                        </v-card>
                    </div>

                    <!-- Step 3 -->
                    <div v-else-if="step === 3">
                        <v-card flat>
                            <v-card-text>
                                <h3>{{ $t('esgPlatform.esgReport.previewReport') }}</h3>
                                <p>{{ $t('esgPlatform.esgReport.previewPlaceholder') }}</p>
                            </v-card-text>
                            <PreviewSection :sections="sections" :protocolId="selectedProtocolId" :subOrganizations="subOrganizations" :targetYears="timeline" @delete-section="deleteSection" />
                            <v-card-actions>
                                <v-btn color="primary" @click="step = 2">{{ $t('esgPlatform.esgReport.back') }}</v-btn>
                                <v-btn color="primary" @click="close">{{ $t('esgPlatform.esgReport.finish') }}</v-btn>
                            </v-card-actions>
                        </v-card>
                    </div>
                </v-card-text>
            </v-card>
        </v-container>
        <v-container v-else-if="reportId && isEditMode" fluid>
            <div class="d-flex mb-4">
                <h2>{{ $t('esgPlatform.esgReport.editEsgReport') }} - {{ originalReportData?.reportId }}</h2>
                <v-spacer></v-spacer>
                <v-btn color="primary" outlined class="mr-2 mb-2"
                     @click="showCloseConfirmation = true">
                     {{ $t('esgPlatform.esgReport.close') }}
                </v-btn>
                <v-btn color="primary" dark class="mr-2 mb-2" @click="showTemplateSettings = true">
                    {{ $t('esgPlatform.esgReport.templateSettings') }}
                </v-btn>
                <v-btn color="primary" dark class="mb-2" @click="showSaveConfirmation = true">
                    {{ $t('esgPlatform.esgReport.saveChanges') }}
                </v-btn>
            </div>
 
            <v-card>
                <v-card-text>
                    <div>
                        <label for="templateName">{{ $t('esgPlatform.esgReport.templateName') }}</label>
                        <div class="my-4">
                            <v-text-field
                                outlined
                                :placeholder="$t('esgPlatform.esgReport.nameOfTemplate')"
                                dense
                                class="shrink"
                                v-model="templateName"
                            ></v-text-field>
                        </div>
                        <div>
                            <SectionItem
                                v-for="(section, idx) in sections"
                                :key="idx"
                                :section="section"
                                :index="idx"
                                :protocol-id="selectedProtocolId"
                                :target-years="timeline"
                                :sub-organizations="subOrganizations"
                                @delete-section="deleteSection"
                                @move-section-up="moveSectionUp"
                                @move-section-down="moveSectionDown"
                                @reorder-blocks="handleReorderBlocks"
                            />
                            <AddNewButton color="green" @click="addSection()">
                                {{ $t('esgPlatform.esgReport.addNewSection') }}
                            </AddNewButton>
                        </div>
                    </div>
                </v-card-text>
            </v-card>
        </v-container>
        <v-container v-else fluid>
            <div class="d-flex justify-space-between align-center mb-4">

                <h2>{{ $t('esgPlatform.esgReport.esgReport') }} {{ reportId }}</h2>
                <div class="d-flex align-center">
                    <!-- v-autocomplete for date -->
                    <v-autocomplete 
                        v-model="timeline" 
                        :items="timelineOptions"
                        :label="$t('esgPlatform.esgReport.selectTimeline')" 
                        class="mr-4" 
                        hide-details 
                        outlined 
                        dense
                        multiple
                        chips
                        deletable-chips
                        small-chips
                        style="max-width: 200px;"
                    ></v-autocomplete>
                    <v-btn color="primary" outlined class="mr-2 btn-outline-primary font-weight-bold mb-2"
                        @click="showCloseConfirmation = true">
                        {{ $t('esgPlatform.esgReport.close') }}
                    </v-btn>
                    <!-- download button with multiple dropwown option -->
                    <template>
                        <v-menu v-model="showDownloadMenu" :close-on-content-click="false" offset-y>
                            <template v-slot:activator="{ on, attrs }">
                                <v-btn color="primary" dark class="mr-2 mb-2" v-bind="attrs" v-on="on">
                                    {{ $t('esgPlatform.esgReport.downloadReport') }}
                                </v-btn>
                            </template>
                            <v-list>
                                <v-list-item @click="downloadReport('pdf')">
                                    <v-list-item-title>{{ $t('esgPlatform.esgReport.downloadAsPdf') }}</v-list-item-title>
                                </v-list-item>
                                <v-list-item @click="downloadReport('pdf-no-images')">
                                    <v-list-item-title>{{ $t('esgPlatform.esgReport.downloadAsPdfNoImages') }}</v-list-item-title>
                                </v-list-item>
                                <!-- <v-list-item @click="downloadReport('xls')">
                                    <v-list-item-title>{{ $t('Download as XLS') }}</v-list-item-title>
                                </v-list-item>
                                <v-list-item @click="downloadReport('csv')">
                                    <v-list-item-title>{{ $t('Download as CSV') }}</v-list-item-title>
                                </v-list-item> -->
                            </v-list>
                        </v-menu>
                    </template>
                </div>
            </div>
            <v-card>
                <v-card-text>
                    <SectionItem v-for="(section, idx) in sectionsGetByIndex" :key="idx" :section="section" :index="idx"
                        :protocol-id="selectedProtocolId" :target-years="timeline" :sub-organizations="subOrganizations"
                        @delete-section="deleteSection" :preview="preview" />
                </v-card-text>
            </v-card>
        </v-container>


        <TemplateSettingsDialog v-model="showTemplateSettings" :settings="templateSettings"
            @save="templateSettings = $event" />
        <ConfirmationDialog :visible="showSaveConfirmation" 
            :title="$t('esgPlatform.esgReport.confirmationTitle')"
            :message="$t('esgPlatform.esgReport.confirmationMessage')" 
            :confirm-text="$t('esgPlatform.esgReport.confirmText')" 
            :cancel-text="$t('esgPlatform.esgReport.cancelText')"
            type="warning" @cancel="showSaveConfirmation = false" @close="showSaveConfirmation = false"
            @confirm="handleSaveChanges" />

        <!-- Explains that editing forked the organization's template into a private copy -->
        <ConfirmationDialog
            :visible="templateForkNotice.show"
            :title="$t('esgPlatform.esgReport.templateCopyCreatedTitle')"
            :message="$t('esgPlatform.esgReport.templateForkedOnEditMessage', { name: templateForkNotice.name })"
            :confirm-text="$t('esgPlatform.esgReport.confirmText')"
            :show-cancel-button="false"
            :show-close-button="false"
            :close-on-overlay="false"
            @confirm="handleTemplateForkNoticeClose"
            @close="handleTemplateForkNoticeClose" />

        <PublishingBlockchainDialog :visible="showDialog" :initialProgress="50" @close="showDialog = false"
            @complete="onPublishComplete" />
        <ConfirmationDialog :visible="showPublishConfirmation" title="Confirmation"
            message="Are you sure you want to publish this report? Once published, the ESG report will be available to the public on Dimitra. No further changes can be made to this report after publishing."
            confirm-text="CONFIRM" cancel-text="CANCEL" type="warning" @confirm="handlePublishReport"
            @cancel="showPublishConfirmation = false" @close="showPublishConfirmation = false" />

        <ConfirmationDialog
            :visible="showCloseConfirmation"
            :title="$t('esgPlatform.esgReport.closeConfirmTitle')"
            :message="$t('esgPlatform.esgReport.closeConfirmMessage')"
            :confirm-text="$t('esgPlatform.esgReport.confirmText')" 
            :cancel-text="$t('esgPlatform.esgReport.cancelText')"
            type="warning"
            @confirm="handleCloseConfirm"
            @cancel="showCloseConfirmation = false"
            @close="showCloseConfirmation = false"
        />
        <!-- Add this snackbar component before closing </v-app> -->
        <v-snackbar
            v-model="snackbar.show"
            :color="snackbar.color"
            :timeout="4000"
            bottom
            right
        >
            {{ snackbar.text }}
            <template v-slot:action="{ attrs }">
                <v-btn
                    text
                    v-bind="attrs"
                    @click="snackbar.show = false"
                >
                    Close
                </v-btn>
            </template>
        </v-snackbar>
    </v-app>
</template>

<script>
import ReportDetails from './components/step1/report-details.vue';
import SubOrganizations from './components/step1/sub-organization.vue';
import TemplateSelection from './components/step1/template-selection.vue';
import SectionItem from './components/step2/SectionItem.vue';
import AddNewButton from './components/step2/AddNewButton.vue';
import TemplateSettingsDialog from './components/step2/TemplateSettingDialog.vue';
import PreviewSection from './components/step3/PreviewSection.vue';
import PublishingBlockchainDialog from '../components/publishingBlockchainDialog.vue';
import ConfirmationDialog from '../components/Confirmation.vue';
import CustomStepper from '../components/CustomStepperTab.vue'; // Add this import
import EsgService from '@/_services/EsgService';
import LoadingMixin from "@/mixins/LoadingMixin";
import { mapGetters } from 'vuex';

export default {
    name: 'CreateReport',
    components: {
        ReportDetails,
        SubOrganizations,
        TemplateSelection,
        SectionItem,
        AddNewButton,
        TemplateSettingsDialog,
        PreviewSection,
        PublishingBlockchainDialog,
        ConfirmationDialog,
        CustomStepper, // Add this component
    },
    computed: {
        ...mapGetters(['isSubEnterprise']),
        // A sub-organization editing the parent organization's original template must
        // not overwrite it — the save forks it into a private copy instead.
        needsTemplateFork() {
            return this.isSubEnterprise
                && !!this.templateInfo
                && !this.templateInfo.subOrganizationId;
        },
        reportSections() {
            return this.sections.map(section => ({
                ...section,
                settings: {
                    ...section.settings,
                    backgroundColor: section.settings.backgroundColor || '#ffffff',
                    fontColor: section.settings.fontColor || '#000000',
                    font: section.settings.font || 'Poppins',
                    timeline: section.settings.timeline || '2026 - 2028',
                    locked: section.settings.locked || false
                }
            }));
        },
        getOrganizationId() {
            return this.$store.getters.getUser?.user_organization?.id;
        },
    },
    async created() {
        this.reportId = this.$route.query.reportId;
        this.currentReportId = this.$route.query.reportId || null;
        this.mode = this.$route.query.mode || 'view';
        this.isEditMode = this.mode === 'edit'; 
        // If arriving with a templateId in the query (e.g., after creation), persist it for steps 2 and 3
        if (this.$route.query.templateId) {
            this.currentTemplateId = this.$route.query.templateId;
        }
        if (this.reportId) {
            this.step = this.isEditMode ? 2 : 3;
            this.startLoading(); // Show loading indicator
            try {
                await this.loadReportData();// Loading report data
                this.preview = !this.isEditMode; // Set preview mode only if not editing
            } catch (error) {
                console.error('Error loading report data:', error);
            } finally {
                this.stopLoading(); // Hide loading indicator
            }
        }
    },
    data() {
        return {
            step: 1,
            reportId: 0,
            selectedProtocol: null,
            selectedProtocolId: null,
            reportType: 'Internal Report',
            timeline: ['2026', '2027'], // Changed from string to array for multi-select
            subOrganizations: [],
            templates: [],
            breadcrumbs: [
                { text: this.$t('esgPlatform.esgReport.breadcrumbEsgPlatform'), to: '/esg-platform/esg-dashboard' },
                { text: this.$t('esgPlatform.esgReport.breadcrumbReportBuilder'), to: '/esg-platform/esg-reports-builder' },
                { text: this.$t('esgPlatform.esgReport.breadcrumbCreateReport'), active: true },
            ],
            selectedTemplate: null,
            sections: [],
            // Bound with v-model in steps 2/edit; must be declared to stay reactive.
            templateName: '',
            dialog: false,
            selectedSection: null,
            showTemplateSettings: false,
            templateSettings: {},
            showDialog: false,
            showPublishConfirmation: false,
            sectionsGetByIndex: [],
            preview: false,
            showDownloadMenu: false,
            showSaveConfirmation: false,
            showCloseConfirmation: false,
            mode: 'view',
            isEditMode: false,
            originalReportData: null,
            stepperSteps: [
                { title: this.$t('esgPlatform.esgReport.stepReportDetails'), tooltip: this.$t('esgPlatform.esgReport.stepReportDetailsTooltip') },
                { title: this.$t('esgPlatform.esgReport.stepCustomizeReport'), tooltip: this.$t('esgPlatform.esgReport.stepCustomizeReportTooltip') },
                { title: this.$t('esgPlatform.esgReport.stepPreview'), tooltip: this.$t('esgPlatform.esgReport.stepPreviewTooltip') }
            ],
            timelineOptions: [
                '2026',
                '2027',
                '2028',
                '2029',
                '2030',
                '2031',
                '2032'
            ],
            snackbar: {
                show: false,
                text: '',
                color: 'success'
            },
            currentTemplateId: null,
            templateInfo: null,
            // The report being worked on, whether it came from the route or was just
            // created in step 1. Needed to relink the report after a template fork.
            currentReportId: null,
            templateForkNotice: { show: false, name: '' },
        };
    },
    methods: {
        handleCloseConfirm() {
        this.showCloseConfirmation = false;
        this.$router.push({ name: 'esgReportsBuilder' });
        },
        handleTemplateForkNoticeClose() {
            this.templateForkNotice.show = false;
            this.$router.push({ name: 'esgReportsBuilder' });
        },
        // Points the report at a different template. PUT /admin/esg-report/:id runs the
        // full esgReportValidationSchema, so reportId / reportType / esgProtocolId /
        // templateId must all be present — sending templateId alone fails validation
        // (and that failure comes back as HTTP 200 with success: false, so it is silent
        // unless the result is checked).
        async relinkReportToTemplate(templateId) {
            const report = this.originalReportData;
            if (!this.currentReportId || !report) return false;
            try {
                const result = await EsgService.updateEsgReport(this.currentReportId, {
                    reportId: report.reportId,
                    reportType: report.reportType,
                    esgProtocolId: report.esgProtocolId?._id || report.esgProtocolId,
                    reportTimeline: report.reportTimeline || [],
                    subOrganizationIds: report.subOrganizationIds,
                    templateId
                });
                return !!result?.success;
            } catch (e) {
                console.error('Failed to relink report to the copied template:', e);
                return false;
            }
        },
        async handleSaveChanges() {
            this.startLoading();
            
            try {
                // Validate required fields
                if (!this.templateName?.trim() || this.sections.length === 0) {
                    this.$notify({
                        type: "error",
                        text: "Template name and sections are required to save",
                    });
                    this.showSaveConfirmation = false;
                    return;
                }

                const templatePayload = {
                    templateName: this.templateName.trim(),
                    templateSettings: this.templateSettings,
                    sections: this.sections
                };

                // Copy-on-write: a sub-organization's customisations go into its own
                // copy so the organization's original template stays untouched.
                let targetTemplateId = this.currentTemplateId;
                let forked = false;

                if (this.needsTemplateFork) {
                    const copyResponse = await EsgService.copyEsgReportTemplate(this.currentTemplateId);
                    if (!copyResponse?.success) {
                        this.$notify({
                            type: "error",
                            text: this.$t('esgPlatform.esgReport.templateCopyFailed'),
                        });
                        this.showSaveConfirmation = false;
                        return;
                    }
                    targetTemplateId = copyResponse.data._id;

                    // Relink the report to the copy BEFORE writing the edits into it, so
                    // that a failure here leaves the organization's original both
                    // unedited and still linked, rather than half-applied.
                    const relinked = await this.relinkReportToTemplate(targetTemplateId);
                    if (!relinked) {
                        this.$notify({
                            type: "error",
                            text: this.$t('esgPlatform.esgReport.templateRelinkFailed'),
                        });
                        this.showSaveConfirmation = false;
                        return;
                    }
                    forked = true;
                }

                const response = await EsgService.updateEsgReportTemplate(targetTemplateId, templatePayload);

                if (!response.success) {
                    this.$notify({
                        type: "error",
                        text: response.message || "Failed to save template. Please try again.",
                    });
                    this.showSaveConfirmation = false;
                    return;
                }

                this.showSaveConfirmation = false;

                if (forked) {
                    this.currentTemplateId = targetTemplateId;
                    this.templateInfo = response.data;
                    if (this.originalReportData) {
                        this.originalReportData.templateId = response.data;
                    }
                    // Tell the user explicitly why a new template appeared, and only
                    // leave the screen once they have acknowledged it.
                    this.templateForkNotice = {
                        show: true,
                        name: response.data?.templateName
                    };
                    return;
                }

                this.$notify({
                    type: "success",
                    text: "Template saved successfully!",
                });
                this.$router.push({ name: 'esgReportsBuilder' });

            } catch (error) {
                console.error('Failed to save template:', error);
                this.$notify({
                    type: "error",
                    text: "An unexpected error occurred. Please try again.",
                });
                this.showSaveConfirmation = false;
            } finally {
                this.stopLoading();
            }
        },
        // Download Report Methods
        async downloadReport(format) {
            if (!this.reportId) {
                this.showError('No report ID available for download');
                return;
            }
 
            if (format === 'pdf') {
                await this.downloadAsPDF(this.reportId, false);
            } else if (format === 'pdf-no-images') {
                await this.downloadAsPDF(this.reportId, true);
            }
            
        },
        async downloadAsPDF(reportId, withoutImages = false) {
            this.startLoading();
            try {
                // Determine the format parameter based on withoutImages flag
                const formatParam = withoutImages ? 'pdf-no-images' : 'pdf';
                
                const response = await EsgService.downloadEsgReport(reportId, formatParam);
 
                // Create blob and download
                const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
                const link = document.createElement('a');
                link.href = url;
                
                // Set filename based on format
                const filename = withoutImages
                    ? `esg-report-${reportId}-no-images.pdf`
                    : `esg-report-${reportId}.pdf`;
                
                link.setAttribute('download', filename);
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
 
                this.showSuccess('Report downloaded successfully!');
                
            } catch (err) {
                console.error('Download error:', err);
                this.showError('Failed to download report. Please try again.');
                this.$notify({
                    type: "error",
                    message: err.message || 'Failed to download report'
                });
            } finally {
                this.stopLoading();
            }
        },
        close() {
            this.$router.push('/');
        },
        openEditor(section = null) {
            this.selectedSection = section;
            this.dialog = true;
        },
        saveSection(newSection) {
            if (this.selectedSection) {
                const idx = this.sections.indexOf(this.selectedSection);
                this.sections.splice(idx, 1, newSection);
            } else {
                this.sections.push(newSection);
            }
        },
        addSection() {
            const next = this.sections.length + 1;
            this.sections.push({
                title: this.$t('esgPlatform.esgReport.section')+ ` ${next}`,
                headingSize: 'H1',
                settings: {
                    backgroundColor: '#ffffff',
                    fontColor: '#000000',
                    font: 'Poppins',
                    timeline: '2026 - 2028',
                    locked: false
                },
                blocks: []
            });
        },
        deleteSection(index) {
            this.sections.splice(index, 1);
        },
        openDimtraDialog() {
            this.$emit('open-dimtra-dialog');
        },
        openBlockchainDialog() {
            this.$emit('open-blockchain-dialog');
        },
        handlePublishReport() {
            this.showPublishConfirmation = false;
            this.$emit('publish-report');
        },
        onPublishComplete() {
            this.showDialog = false;
            this.$emit('publish-complete');
        },
        handleProtocolSelection(protocolId) {
            this.selectedProtocolId = protocolId;
        },
        async loadReportData() {
            // Loading data
            const result = await EsgService.getEsgReportById(this.reportId);

            if (result?.success) {
                // Store original data for reference
                this.originalReportData = JSON.parse(JSON.stringify(result.data));
                
                if (this.isEditMode) {
                    // Load data into edit form (step 2)
                        this.sections = result.data.templateId.sections.map(section => ({
                        ...section,
                        blocks: section.blocks.map(block => ({
                            ...block, content: block.content || '',
                            settings: block.settings || { bgColor: '#ffffff', fontColor: '#101010', font: 'Poppins' }
                        }))
                    }));
                    // this.sections = result.data.templateId.sections;
                    this.templateSettings = result.data.templateId.templateSettings;
                    this.templateName = result.data.templateId.templateName;
                    this.currentTemplateId = result.data.templateId._id;
                    // Needed to decide whether saving must fork the template.
                    this.templateInfo = result.data.templateId;
                } else {
                    // Load data for preview (step 3)
                this.sectionsGetByIndex = result.data.templateId.sections;
                this.templateSettings = result.data.templateId.templateSettings;
                }
                this.timeline = result.data.reportTimeline;
                this.reportType = result.data.reportType === 'private' ? 'Internal Report': 'public';
                this.subOrganizations = result.data.subOrganizationIds;
                // Set protocolId if available in the report data
                if (result.data.esgProtocolId) {
                    this.selectedProtocolId = result.data.esgProtocolId;
                }
            } else {
                this.$notify({
                    type: "error",
                    message: "Failed to load report!"
                });
                this.$router.go(-1);
                throw new Error('Failed to load report data');
            }
        },
        handleStepChange(newStep) {
            this.step = newStep;
            if (newStep === 2) {
                this.loadTemplateInfo();
            }
        },
        showSuccess(message) {
            this.snackbar = {
                show: true,
                text: message,
                color: 'success'
            };
        },
        showError(message) {
            this.snackbar = {
                show: true,
                text: message,
                color: 'error'
            };
        },
        async handleCreateEsg() {
            try {
                // Validate required fields first
                if (!this.selectedProtocolId) {
                    this.showError('Please select a protocol first');
                    return;
                }

                // suborganizations should not be empty
                if (this.subOrganizations.length === 0) {
                    this.showError('Please select at least one sub-organization');
                    return;
                }

                // Validate timeline selection
                if (this.timeline.length === 0) {
                    this.showError('Please select at least one timeline year');
                    return;
                }
                
                // template id should be 
                if (!this.selectedTemplate) {
                    this.showError('Please select a template');
                    return;
                }

                // Generate a unique report ID
                const reportId = `ESG-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
                const organizationId = this.getOrganizationId;

                const reportPayload = {
                    reportId: reportId,
                    reportType: this.reportType === 'Internal Report' ? 'private' : 'public',
                    dateGenerated: new Date().toISOString(),
                    status: 'saved',
                    esgProtocolId: this.selectedProtocolId,
                    reportTimeline:  JSON.parse(JSON.stringify(this.timeline)), // Clean the reactive array
                    subOrganizationIds: this.subOrganizations,
                    templateId: this.selectedTemplate,
                    organizationId: organizationId,
                };

                console.log('ESG Report Payload:', reportPayload);
                this.startLoading();
                const response = await EsgService.createEsgReport(reportPayload);
                console.log('ESG Report Creation Response:', response);

                if (response?.success === true) {
                    // Preserve selected template id for subsequent steps before resetting form-specific state
                    const createdTemplateId = this.selectedTemplate;
                    this.resetPayloadData();
                    this.currentTemplateId = createdTemplateId;
                    this.currentReportId = response.data?._id || null;
                    // Kept so step 2 can resend the report's required fields if saving
                    // has to relink it to a forked template copy.
                    this.originalReportData = response.data || null;
                    this.$router.replace({
                        query: { ...this.$route.query, templateId: createdTemplateId }
                    });
                    // Load existing template info to prefill step 2 with its sections/settings
                    await this.loadTemplateInfo();
                    this.step = 2;
                    this.showSuccess('ESG Report created successfully!');
                    this.stopLoading();
                } else {
                    console.error('Error creating ESG Report:', response.message);
                    this.showError('Failed to create ESG Report. Please try again.');
                    this.stopLoading();
                    return;
                }

            } catch (error) {
                console.error('Error creating ESG Report:', error);
                this.showError('An error occurred while creating the report. Please try again.');
                this.stopLoading();
            }
        },
        resetPayloadData() {
            this.selectedProtocol = null;
            this.selectedProtocolId = null;
            this.reportType = 'Internal Report';
            this.timeline = []; // Reset to empty array
            this.subOrganizations = [];
            this.templates = [];
            this.selectedTemplate = null;
            this.sections = [];
        },
        async loadTemplateInfo() {
            const templateId = this.currentTemplateId || this.$route.query.templateId;
            if (!templateId) return;
            this.startLoading();
            try {
                if (templateId) {
                    const response = await EsgService.getEsgReportTemplateById(templateId);

                    if (!response.success) {
                        this.$router.back();
                        return;
                    }

                    this.templateName = response.data.templateName;
                    this.templateSettings = response.data.templateSettings;
                    this.sections = response.data.sections;
                    this.templateInfo = response.data;
                }
            } catch (e) {
                console.error('Failed to load template info:', e);
            }
            finally {
                this.stopLoading();
            }
        },
        moveSectionUp(index) {
            if (index <= 0) return;
            const temp = this.sections[index];
            this.$set(this.sections, index, this.sections[index - 1]);
            this.$set(this.sections, index - 1, temp);
                        const changedSections = [
                { _id: this.sections[index - 1]?._id, order: index - 1 },
                { _id: this.sections[index]?._id, order: index }
            ].filter(s => s._id); 
            this.saveReorder({ sections: changedSections });
        },
        moveSectionDown(index) {
            if (index >= this.sections.length - 1) return;
            const temp = this.sections[index];
            this.$set(this.sections, index, this.sections[index + 1]);
            this.$set(this.sections, index + 1, temp);
            const changedSections = [
                { _id: this.sections[index]?._id, order: index },
                { _id: this.sections[index + 1]?._id, order: index + 1 }
            ].filter(s => s._id);
            this.saveReorder({ sections: changedSections });
        },
        handleReorderBlocks({ sectionId, swappedBlocks }) {
            if (!sectionId || !swappedBlocks || swappedBlocks.length === 0) return;
            const changedBlocks = swappedBlocks
                .filter(b => b._id)
                .map(b => ({
                    _id: b._id,
                    sectionId: sectionId,
                    order: b.order
                }));
            this.saveReorder({ blocks: changedBlocks });
        },
        async saveReorder(changedData = {}) {
            if (!this.currentTemplateId) return;
            
            try {
                const payload = {};
                if (changedData.sections && changedData.sections.length > 0) {
                    payload.sections = changedData.sections;
                }
                if (changedData.blocks && changedData.blocks.length > 0) {
                    payload.blocks = changedData.blocks;
                }
                if (Object.keys(payload).length > 0) {
                    await EsgService.reOrderEsgReportTemplate(this.currentTemplateId, payload);
                }
            } catch (error) {
                console.error('Failed to save reorder:', error);
                this.showError('Failed to save order. Please try again.');
            }
        },
    },
      mixins: [ LoadingMixin ],
};
</script>

<style scoped>
.v-stepper {
    box-shadow: none !important;
}

.v-card__text {
    padding: 6px !important;
}

.v-stepper__content {
    padding: 0px !important;
}
</style>